import type { ICustomMessage } from '@/components/chat';
import { isSameUserId } from '@/utils/isSameUserId';

/**
 * Window after `createdAt` during which we treat a recipient-author
 * message as still-pending even if `processingStatus` hasn't arrived
 * yet. Covers the brief gap between Twilio acking the send (the
 * message lands in the chat cache) and the backend writing the first
 * `pipelineStatus` (`reviewing`). 8s is generous — backend typically
 * emits within 1–2s; after the window expires with no status, we
 * conclude the pipeline isn't running for this message and the real
 * bubble takes over.
 */
const SEND_GRACE_PERIOD_MS = 8000;

function toCreatedAtMs(createdAt: Date | number | undefined): number | null {
  if (createdAt instanceof Date) {
    return createdAt.getTime();
  }

  if (typeof createdAt === 'number') {
    return createdAt;
  }

  return null;
}

/**
 * Pure predicate shared between `renderBubble` (which mounts the
 * skeleton + status surfaces) and `renderMessage` (which removes the
 * right margin on the outer row container so the skeleton sits flush
 * with the chat edge). Keeping the rule in one place avoids the two
 * call sites drifting apart.
 *
 * The inquiry submitter (`isAuthor`) is always excluded — they should
 * see their own outgoing message land immediately rather than be
 * replaced by a loading placeholder. Recipients (typically the expert
 * answering the inquiry) still get the skeleton while the pipeline
 * runs, regardless of their `userType`.
 */
export function isOutgoingMessageProcessingPending(
  currentMsg: ICustomMessage,
  ctx: {
    userId: string;
    isAuthor: boolean;
  }
): boolean {
  if (ctx.isAuthor) {
    return false;
  }

  if (!isSameUserId(currentMsg.user?._id, ctx.userId)) {
    return false;
  }

  if (currentMsg.approved === true) {
    return false;
  }

  const processingStatus = currentMsg.processingStatus;

  if (processingStatus !== undefined) {
    return processingStatus !== 'complete';
  }

  const msgCreatedAtMs = toCreatedAtMs(currentMsg.createdAt);

  return msgCreatedAtMs !== null && Date.now() - msgCreatedAtMs < SEND_GRACE_PERIOD_MS;
}
